// Trạng thái đăng nhập của trình duyệt: unknown (đang thử làm mới bằng cookie) → authenticated | anonymous.
import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { http, refreshSession, sessionEnded, signIn, signOut } from '@/shared/api';

export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous';
let status: SessionStatus = 'unknown';
let endReason: string | undefined;
const ls = new Set<() => void>();

/**
 * Dấu phiên của trình duyệt (không chứa bí mật — cookie refresh là HttpOnly): '1' sau khi đăng nhập / làm mới thành công,
 * '0' sau khi đăng xuất / phiên kết thúc / làm mới thất bại. '0' ⇒ mở ứng dụng không gọi /auth/refresh (gateway trả 401
 * khi không có cookie — trình duyệt ghi lỗi đỏ ở console mỗi lần vào màn đăng nhập). Vắng (lần đầu, trình duyệt chặn bộ
 * nhớ) ⇒ vẫn thử làm mới như cũ.
 */
const HINT = 'cathouse.session';
const readHint = () => {
  try {
    return localStorage.getItem(HINT);
  } catch {
    return null;
  }
};
const writeHint = (s: SessionStatus) => {
  if (s === 'unknown') return;
  try {
    localStorage.setItem(HINT, s === 'authenticated' ? '1' : '0');
  } catch {
    /* bỏ qua */
  }
};

const set = (s: SessionStatus, reason?: string) => {
  status = s;
  endReason = reason;
  writeHint(s);
  ls.forEach((l) => l());
};
sessionEnded.on(({ reason }) => set('anonymous', reason));

export const session = {
  get status() {
    return status;
  },
  get endReason() {
    return endReason;
  },
  /** Gọi một lần khi mở ứng dụng: còn cookie refresh hợp lệ ⇒ vào thẳng; lần trước đã đăng xuất ⇒ không hỏi. */
  async restore() {
    if (status !== 'unknown') return status;
    if (readHint() === '0') {
      set('anonymous');
      return status;
    }
    set((await refreshSession()) ? 'authenticated' : 'anonymous');
    return status;
  },
  async login(input: { tenantSlug: string; email: string; password: string }) {
    const r = await signIn('/auth/login', input);
    set('authenticated');
    return r;
  },
  async register(input: { tenantSlug: string; email: string; displayName: string; password: string }) {
    const r = await signIn('/auth/register', input);
    set('authenticated');
    return r;
  },
  async createCompany(input: { slug: string; ownerName: string; ownerEmail: string; ownerPassword: string }) {
    const r = await signIn('/tenants', input);
    set('authenticated');
    return r;
  },
  /** Về từ Elite Accounts (/dang-nhap/sso): gateway vừa đặt cookie refresh ⇒ bỏ dấu "đã đăng xuất" và làm mới ngay. */
  async completeSso() {
    set((await refreshSession()) ? 'authenticated' : 'anonymous');
    return status;
  },
  /** Đăng nhập qua Elite Accounts ⇒ sau khi đăng xuất chuyển về trang Accounts (phiên Accounts do Accounts quản). */
  async logout(all = false) {
    await signOut(all).catch(() => undefined);
    set('anonymous');
    const sso = await fetchSsoConfig();
    if (sso.enabled) window.location.assign(sso.accountsUrl);
  },
  subscribe(l: () => void) {
    ls.add(l);
    return () => ls.delete(l);
  },
};

export const useSessionStatus = () => useSyncExternalStore(session.subscribe, () => status);

// ---- Đăng nhập bằng tài khoản Elite (SSO qua Elite Accounts)
export type SsoConfig = { enabled: false } | { enabled: true; tenantSlug: string; accountsUrl: string };

/** Cấu hình SSO của máy chủ; lỗi / chưa có ⇒ coi như tắt (giữ đăng nhập mật khẩu). */
export async function fetchSsoConfig(): Promise<SsoConfig> {
  try {
    const c = await http.get<SsoConfig>('/auth/sso/config');
    return c?.enabled === true && c.tenantSlug && /^https?:\/\//.test(c.accountsUrl) ? c : { enabled: false };
  } catch {
    return { enabled: false };
  }
}
export const useSsoConfig = () => useQuery({ queryKey: ['auth', 'sso-config'], queryFn: fetchSsoConfig, staleTime: 5 * 60_000 });

/** Chỉ đường dẫn nội bộ (giống gateway): một "/" ở đầu, không "//", không "\", không ký tự điều khiển. */
export function safeNext(raw: unknown): string {
  if (typeof raw !== 'string' || raw.length > 500) return '/';
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\') || /[\u0000-\u001f\u007f]/.test(raw)) return '/';
  try {
    const u = new URL(raw, 'http://cathouse.invalid');
    const out = `${u.pathname}${u.search}${u.hash}`;
    // Kiểm lại SAU khi chuẩn hoá: "/.//x", "/%2e//x" thành "//x" (URL tương đối giao thức)
    return u.origin === 'http://cathouse.invalid' && out.startsWith('/') && !out.startsWith('//') && !out.includes('\\') ? out : '/';
  } catch {
    return '/';
  }
}
export const ssoStartUrl = (next = '/') => `/api/auth/sso/start?next=${encodeURIComponent(safeNext(next))}`;

const SSO_ERRORS: Record<string, string> = {
  'khong-co-quyen': 'Tài khoản Elite của bạn chưa được cấp quyền dùng CatHouse. Liên hệ quản trị để được cấp.',
  'accounts-tam-loi': 'Hệ thống tài khoản Elite đang tạm gián đoạn. Vui lòng thử lại sau ít phút.',
  'accounts-tu-choi': 'Đăng nhập đã bị huỷ ở trang tài khoản Elite.',
  'phien-dang-nhap-het-han': 'Lượt đăng nhập đã quá hạn. Vui lòng bấm đăng nhập lại.',
  'tai-khoan-bi-khoa': 'Tài khoản của bạn trong CatHouse đã bị vô hiệu hoá. Liên hệ quản trị.',
  'trung-tai-khoan': 'Email này đã gắn với một tài khoản Elite khác. Liên hệ quản trị.',
};
export const ssoErrorText = (code: string) => SSO_ERRORS[code] ?? 'Không đăng nhập được. Vui lòng thử lại.';

const LAST_TENANT = 'cathouse.tenant';
export const lastTenant = {
  get: () => {
    try {
      return localStorage.getItem(LAST_TENANT) ?? '';
    } catch {
      return '';
    }
  },
  set: (slug: string) => {
    try {
      localStorage.setItem(LAST_TENANT, slug);
    } catch {
      /* bỏ qua */
    }
  },
};
